import type { AvailabilityType, AvailableSlot, OperatingWindow } from '../../types';
import { DateSelector } from '../../components/DateSelector';
import { TimeSlotSelector } from '../../components/TimeSlotSelector';
import { trackEvent } from '../../utils/track';
import { validateContinuousTime } from '../../utils/availabilityValidation';

interface Props {
  date: string;
  startsAt: string;
  partySize: number;
  availabilityType: AvailabilityType;
  slots: AvailableSlot[];
  operatingWindow: OperatingWindow | null;
  loadingSlots: boolean;
  mode: 'instant' | 'request';
  experienceCode: string;
  bookingType: string;
  onDateChange: (v: string) => void;
  onSlotChange: (v: string) => void;
  onNext: () => void;
}

export function DateTimeStep({
  date,
  startsAt,
  partySize,
  availabilityType,
  slots,
  operatingWindow,
  loadingSlots,
  mode,
  experienceCode,
  bookingType,
  onDateChange,
  onSlotChange,
  onNext,
}: Props) {
  const handleDateChange = (v: string) => {
    onDateChange(v);
    if (v) {
      trackEvent('booking_date_selected', { experienceCode, bookingType, eventDate: v });
    }
  };

  const handleSlotChange = (v: string) => {
    onSlotChange(v);
    trackEvent('booking_slot_selected', {
      experienceCode,
      bookingType,
      eventDate: date,
      eventTime: v,
    });
  };

  // Em Continuous, o horário precisa estar dentro de [start, end). Em SlotBased,
  // o slot já vem do server — só checamos preenchimento. O backend revalida nos
  // dois casos (defesa em profundidade), mas bloquear aqui dá feedback imediato
  // e evita um round-trip inútil.
  const continuousCheck =
    availabilityType === 'Continuous'
      ? validateContinuousTime(startsAt, operatingWindow)
      : { valid: true, error: null };
  const canContinue = !!date && !!startsAt && continuousCheck.valid;

  return (
    <div className="step">
      <h2 className="step__title">Quando você quer ir?</h2>
      <p className="step__lead">
        {mode === 'request'
          ? 'Escolha sua data e horário desejado. O operador confirma em até 24h.'
          : 'Escolha a data e o horário disponíveis.'}
      </p>

      <DateSelector value={date} onChange={handleDateChange} />
      {date && (
        <TimeSlotSelector
          availabilityType={availabilityType}
          slots={slots}
          operatingWindow={operatingWindow}
          value={startsAt || null}
          onChange={handleSlotChange}
          loading={loadingSlots}
          partySize={partySize}
          mode={mode}
          date={date}
        />
      )}

      <div className="step__cta-row">
        <button
          type="button"
          className="btn btn--brand btn--lg btn--block"
          onClick={onNext}
          disabled={!canContinue}
        >
          Continuar
        </button>
        {!canContinue && continuousCheck.error && startsAt && (
          <p className="step__cta-hint" role="status">
            {continuousCheck.error}
          </p>
        )}
      </div>
    </div>
  );
}
